import { useState, useMemo } from "react";
import { Calculator, Moon, Sun, Phone, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { useLanguage } from "@/context/LanguageContext";

const NIGHT_SHIFT_MULTIPLIER = 1.20;
const WEEKEND_MULTIPLIER = 1.15;
const WEEKS_PER_MONTH = 4;

// Hourly rate ranges by service type
const BASIC_RATE = { min: 20, max: 28, avg: 24 };
const PERSONAL_RATE = { min: 24, max: 35, avg: 30 };
const SPECIALIZED_RATE = { min: 30, max: 50, avg: 40 };

const RATES = {
  basic: BASIC_RATE,
  personal: PERSONAL_RATE,
  specialized: SPECIALIZED_RATE,
};

const QuoteInputs = ({ serviceType, setServiceType, hoursPerVisit, setHoursPerVisit, visitsPerWeek, setVisitsPerWeek, nightShift, setNightShift, weekend, setWeekend, t }) => {
  const hoursValue = useMemo(() => [hoursPerVisit], [hoursPerVisit]);
  const visitsValue = useMemo(() => [visitsPerWeek], [visitsPerWeek]);

  return (
    <Card className="lg:col-span-3 border-0 shadow-xl">
      <CardHeader>
        <CardTitle className="flex items-center gap-3 text-[#1A2332]">
          <Calculator className="w-6 h-6 text-[#003A4B]" />
          {t.calculator.serviceCalc}
        </CardTitle>
        <p className="text-[#718096]">{t.calculator.calcRealTime}</p>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label className="text-[#1A2332] font-medium">{t.calculator.serviceType}</Label>
          <Select value={serviceType} onValueChange={setServiceType}>
            <SelectTrigger data-testid="service-type-select" className="border-[#003A4B]/20 focus:ring-[#003A4B]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="basic">{t.calculator.basicCare}</SelectItem>
              <SelectItem value="personal">{t.calculator.personalCare}</SelectItem>
              <SelectItem value="specialized">{t.calculator.specializedCare}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-3">
          <div className="flex justify-between">
            <Label className="text-[#1A2332] font-medium">{t.calculator.hoursPerVisit}</Label>
            <span className="text-[#003A4B] font-bold">{hoursPerVisit}h</span>
          </div>
          <Slider data-testid="hours-slider" value={hoursValue} onValueChange={(v) => setHoursPerVisit(v[0])} min={4} max={12} step={1} className="[&_[role=slider]]:bg-[#003A4B]" />
        </div>

        <div className="space-y-3">
          <div className="flex justify-between">
            <Label className="text-[#1A2332] font-medium">{t.calculator.visitsPerWeek}</Label>
            <span className="text-[#003A4B] font-bold">{visitsPerWeek}</span>
          </div>
          <Slider data-testid="visits-slider" value={visitsValue} onValueChange={(v) => setVisitsPerWeek(v[0])} min={1} max={7} step={1} className="[&_[role=slider]]:bg-[#003A4B]" />
        </div>

        <div className="flex flex-wrap gap-6 pt-2">
          <div className="flex items-center gap-3">
            <Switch data-testid="night-shift-toggle" checked={nightShift} onCheckedChange={setNightShift} className="data-[state=checked]:bg-[#003A4B]" />
            <Label className="flex items-center gap-2 cursor-pointer"><Moon className="w-4 h-4 text-[#718096]" />{t.calculator.nightShift}</Label>
          </div>
          <div className="flex items-center gap-3">
            <Switch data-testid="weekend-toggle" checked={weekend} onCheckedChange={setWeekend} className="data-[state=checked]:bg-[#003A4B]" />
            <Label className="flex items-center gap-2 cursor-pointer"><Sun className="w-4 h-4 text-[#718096]" />{t.calculator.weekend}</Label>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const QuoteResult = ({ quote, t }) => (
  <Card className="lg:col-span-2 border-0 shadow-xl bg-gradient-to-br from-[#003A4B] to-[#1B5E6B] text-white">
    <CardHeader>
      <CardTitle className="text-white">{t.calculator.estimatedQuote}</CardTitle>
      <p className="text-white/70">{t.calculator.rate}: ${quote.hourlyRate}/hr x {quote.totalHours}h/week</p>
    </CardHeader>
    <CardContent className="space-y-6">
      <div className="space-y-4">
        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center">
          <p className="text-white/70 text-sm mb-1">{t.calculator.perWeek}</p>
          <p data-testid="weekly-rate" className="text-4xl font-bold text-[#C89D4B]">${quote.weeklyRate}</p>
        </div>
        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center">
          <p className="text-white/70 text-sm mb-1">{t.calculator.perMonth}</p>
          <p data-testid="monthly-rate" className="text-4xl font-bold text-[#C89D4B]">${quote.monthlyRate}</p>
        </div>
      </div>
      <div className="space-y-3">
        <a href="https://wa.me/16093153943" target="_blank" rel="noopener noreferrer" className="block">
          <Button data-testid="quote-whatsapp-btn" className="w-full bg-[#C89D4B] text-white hover:bg-[#B38A3E] rounded-full py-6 font-semibold">
            <MessageCircle className="w-5 h-5 mr-2" />{t.calculator.requestWhatsApp}
          </Button>
        </a>
        <a href="tel:+16093153943" className="block">
          <Button data-testid="quote-call-btn" variant="outline" className="w-full border-2 border-white text-white hover:bg-white/10 rounded-full py-6 font-semibold">
            <Phone className="w-5 h-5 mr-2" />{t.calculator.callNow}
          </Button>
        </a>
      </div>
      <p className="text-center text-white/60 text-xs">{t.calculator.minimum}</p>
    </CardContent>
  </Card>
);

export const CalculatorSection = () => {
  const { t } = useLanguage();
  const [serviceType, setServiceType] = useState("personal");
  const [hoursPerVisit, setHoursPerVisit] = useState(4);
  const [visitsPerWeek, setVisitsPerWeek] = useState(3);
  const [nightShift, setNightShift] = useState(false);
  const [weekend, setWeekend] = useState(false);

  const quote = useMemo(() => {
    const baseRate = RATES[serviceType].avg;
    const totalHours = hoursPerVisit * visitsPerWeek;
    let weeklyRate = baseRate * totalHours;
    if (nightShift) weeklyRate *= NIGHT_SHIFT_MULTIPLIER;
    if (weekend) weeklyRate *= WEEKEND_MULTIPLIER;
    return { hourlyRate: baseRate, totalHours, weeklyRate: weeklyRate.toFixed(2), monthlyRate: (weeklyRate * WEEKS_PER_MONTH).toFixed(2) };
  }, [serviceType, hoursPerVisit, visitsPerWeek, nightShift, weekend]);

  return (
    <section id="calculator" data-testid="calculator-section" className="py-24 bg-[#F3F0E7]">
      <div className="max-w-5xl mx-auto px-6 lg:px-12">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-[#C89D4B] font-semibold uppercase tracking-widest text-sm">{t.calculator.title}</span>
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-[#1A2332] mt-3">{t.calculator.subtitle}</h2>
        </div>
        <div className="grid lg:grid-cols-5 gap-8">
          <QuoteInputs serviceType={serviceType} setServiceType={setServiceType} hoursPerVisit={hoursPerVisit} setHoursPerVisit={setHoursPerVisit} visitsPerWeek={visitsPerWeek} setVisitsPerWeek={setVisitsPerWeek} nightShift={nightShift} setNightShift={setNightShift} weekend={weekend} setWeekend={setWeekend} t={t} />
          <QuoteResult quote={quote} t={t} />
        </div>
      </div>
    </section>
  );
};
